import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useBoxPrograms } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { BackgroundProfileIcon } from "@images/backgroundProfileIcon"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { PencilEditIcon } from "@images/svgIcons/pencilEditIcon"
import {
  BoxOfferArchivedModal,
  type BoxOfferArchivedModalRef,
} from "@modules/boxOffer/components/boxOfferArchivedModal"
import { boxOffersInformation } from "@modules/boxOffer/components/boxOfferCardsView"
import {
  BoxOfferFormattedPrice,
  BoxOfferInputCustomLabel,
  BoxOfferRuleCard,
} from "@modules/boxOffer/components/boxOfferFormSection"
import { BoxOfferDetailedEntity, BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { getOfferCommitment, getOfferVisibility } from "@modules/boxOffer/utils/boxOfferUtils"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { capitalize, shortenText } from "@modules/utils/stringUtils"
import { Archive, LoaderCircle } from "lucide-react"
import { useCallback, useMemo, useRef } from "react"

type BoxOfferResumeViewProps = {
  formOffer?: BoxOfferDraftEntity // use to create and update offer
  offerId?: string // use to show offer
  rightButtonAction?: () => void
  leftButtonAction?: () => void
  loading?: boolean
}

export const BoxOfferResumeView = ({
  formOffer,
  offerId,
  rightButtonAction,
  leftButtonAction,
  loading,
}: BoxOfferResumeViewProps) => {
  const { navigateToBoxOfferForm } = useBoxOfferSidebar()
  const canEditOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_OFFER)
  const { data: programs } = useBoxPrograms()
  const { data: boxOffer } = useBoxOffer(offerId)
  const offer = formOffer ?? boxOffer
  const boxOfferArchivedModal = useRef<BoxOfferArchivedModalRef>(null)

  const offerInformation = useMemo(
    () => boxOffersInformation().find((infos) => infos.type === offer?.type),
    [offer?.type],
  )
  const filteredPrograms = useMemo(
    () => programs.filter((program) => offer?.programs.includes(program.id)),
    [offer?.programs, programs],
  )

  const handleArchiveOffer = () => {
    if (offer instanceof BoxOfferDetailedEntity) {
      boxOfferArchivedModal.current?.open()
    }
  }

  const handleReactiveOffer = async () => {
    if (offer) {
      offer.setArchived(false)
      await navigateToBoxOfferForm(undefined, offer as BoxOfferDetailedEntity)
    }
  }

  const offerCommitment = useMemo(() => {
    return getOfferCommitment(offer)
  }, [offer])

  const visibilitySubtitle = useMemo(() => {
    return getOfferVisibility(offer, "var(--textDefault-body)")
  }, [offer])

  const handleRightAction = useCallback(() => {
    if (rightButtonAction) {
      rightButtonAction()
      return
    }
    if (offer?.archived) {
      void navigateToBoxOfferForm(undefined, offer as BoxOfferDetailedEntity, true)
    } else {
      void navigateToBoxOfferForm(undefined, offer as BoxOfferDetailedEntity)
    }
  }, [navigateToBoxOfferForm, offer, rightButtonAction])

  return offerInformation && offer ? (
    <>
      <div className="flex flex-col gap-8 pb-4">
        {/* HEADER */}
        <div className="mb-5 flex h-75 rounded-b-2xl bg-neutral-50">
          <div className="m-auto flex w-[56%] flex-col items-center">
            {offerInformation.icon}
            <Text fontStyle="HS" className="mt-2 text-center break-all">
              {offer.name}
            </Text>
            <Text fontStyle="BM" className="text-center text-neutral-700">
              {capitalize(offerCommitment)}
              {offer.commitmentPeriodAutoRenewal
                ? `- ${word("box.offer.form.infos.commitmentPeriodAutoRenewal.active")}`
                : ""}
            </Text>
            <Text
              fontStyle="BS"
              className="my-1 flex items-center justify-center gap-1 wrap-break-word whitespace-break-spaces text-neutral-700"
            >
              {visibilitySubtitle.icon}
              {visibilitySubtitle.text}
            </Text>

            <BoxOfferFormattedPrice
              price={
                typeof offer.price === "number"
                  ? ({ value: offer.price, currency: offer.currency } as PriceObjectEntity)
                  : offer.price
              }
              showRecurringPeriod={offer.type === BoxOfferType.Subscription}
              recurringPeriod={offer.type === BoxOfferType.Subscription ? offer.recurringPeriod : undefined}
              periodStyle="TS"
            />
          </div>

          <div
            className={`w-[40%] overflow-hidden ${offer.archived ? "bg-neutral-50" : "bg-primary-200"}`}
            style={{ borderBottomRightRadius: "1rem", clipPath: "polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%)" }}
          >
            <BackgroundProfileIcon
              className={"h-full bg-cover bg-no-repeat " + (offer.archived ? "text-neutral-300" : "text-primary-100")}
              color="currentColor"
              style={{ objectFit: "cover" }}
            />
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex flex-col gap-5 p-6 pt-0">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces text-neutral-700">
            {offer.description}
          </Text>

          {offer.type !== BoxOfferType.DropIn && (
            <div
              className={`flex justify-between gap-5 ${
                ![BoxOfferType.Subscription, BoxOfferType.Millenium].includes(offer.type) ? "text-center" : "flex-col"
              }`}
            >
              {[BoxOfferType.TicketsBook, BoxOfferType.Trial].includes(offer.type) && (
                <div className="w-full">
                  <BoxOfferInputCustomLabel label={word("offer.form.numberOfSessions.title")} />
                  <BoxOfferRuleCard $borderSize={2} $resume={true}>
                    <Text className="w-full text-center" fontStyle="TS">
                      {word("offer.form.numberOfSessions.included", { count: offer.count ?? 0 })}
                    </Text>
                  </BoxOfferRuleCard>
                </div>
              )}

              <div className="w-full">
                <BoxOfferInputCustomLabel label={word("box.offer.form.infos.restrictedPeriod.title")} />
                <div
                  style={{
                    display: "grid",
                    gridAutoFlow: [BoxOfferType.Subscription, BoxOfferType.Millenium].includes(offer.type)
                      ? "column"
                      : "row",
                    gap: "0.6rem",
                    gridTemplateColumns: [BoxOfferType.Subscription, BoxOfferType.Millenium].includes(offer.type)
                      ? "repeat(auto-fit, minmax(0, 1fr))"
                      : undefined,
                  }}
                >
                  {offer.rules && offer.rules.length > 0 ? (
                    offer.rules.map((rule, index) => (
                      <BoxOfferRuleCard key={index} $borderSize={2} $resume={true}>
                        <Text className="w-full text-center" fontStyle="TS">{`${String(rule.value)} ${word(
                          "offer.form.rules.sessions",
                          { count: rule.value },
                        )} ${word(`offer.form.rules.period.per.${rule.period}`)}`}</Text>
                      </BoxOfferRuleCard>
                    ))
                  ) : (
                    <BoxOfferRuleCard $borderSize={2} $resume={true}>
                      <Text className="w-full text-center" fontStyle="TS">
                        {word("box.offer.form.infos.rules.unlimited")}
                      </Text>
                    </BoxOfferRuleCard>
                  )}
                </div>
              </div>

              {BoxOfferType.Trial === offer.type && (
                <div className="w-full">
                  <BoxOfferInputCustomLabel label={word("box.offer.form.infos.renewal.title")} />
                  <BoxOfferRuleCard $borderSize={2}>
                    <Text className="w-full text-center" fontStyle="TS">
                      {offer.renewable
                        ? word("box.offer.form.infos.renewal.yes")
                        : word("box.offer.form.infos.renewal.no")}
                    </Text>
                  </BoxOfferRuleCard>
                </div>
              )}
            </div>
          )}

          <div className="mb-5">
            <BoxOfferInputCustomLabel label={word("box.offer.form.infos.activitiesIncluded.title")} />
            <div className="min-h-[100px] rounded-2xl border-2 border-solid border-borderPrimary bg-neutral-100 px-5 py-4">
              <Text fontStyle="BS">{word("box.offer.resume.activitiesIncluded.details")}</Text>

              <div className="mt-3 flex flex-wrap gap-2">
                {filteredPrograms.map((program, index) =>
                  program.name.length > 65 ? (
                    <>
                      <Tooltip key={`tooltip-program-${String(index)}`}>
                        <TooltipTrigger
                          render={
                            <Badge
                              key={`badge-program-${String(index)}`}
                              variant="neutralIntense"
                              size="lg"
                              className="wrap-break-word whitespace-break-spaces"
                            >
                              {shortenText(program.name, 28)}
                            </Badge>
                          }
                        />
                        <TooltipContent side="bottom" className="w-fit max-w-60 wrap-break-word whitespace-normal">
                          {program.name}
                        </TooltipContent>
                      </Tooltip>
                    </>
                  ) : (
                    <Badge key={`badge-program-${String(index)}`} variant="neutralIntense" size="lg">
                      {program.name}
                    </Badge>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
          {canEditOffer && (
            <div className={`mb-4 flex gap-5 ${offer.archived && !formOffer ? "flex-row-reverse" : "flex-row"}`}>
              {offer.archived && !formOffer ? (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => void handleReactiveOffer()}
                  className="grow px-3 py-2"
                  disabled={loading}
                >
                  {word("global.reactivate")}
                  <CheckmarkIcon color="var(--textDefault-body)" width={22} height={22} />
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={leftButtonAction ?? handleArchiveOffer}
                  className="grow px-3 py-2"
                  disabled={loading}
                >
                  {formOffer ? (
                    <ArrowIcon arrowDirection="left" color="var(--textDefault-body)" width={14} height={14} />
                  ) : (
                    <Archive strokeWidth={1.5} size={15} color="var(--textDefault-body)" />
                  )}
                  {leftButtonAction ? word("global.previous") : word("global.archive")}
                </Button>
              )}

              <Button
                variant={offer.archived ? "secondary" : "primary"}
                size="lg"
                onClick={handleRightAction}
                className="grow px-3 py-2"
                disabled={loading}
                isLoading={loading}
                type={formOffer ? "submit" : "button"}
              >
                {formOffer ? word("global.save") : word("global.edit")}
                {!formOffer && <PencilEditIcon color="var(--textDefault-body)" />}
                {loading && <LoaderCircle className="animate-spin" />}
              </Button>
            </div>
          )}
        </div>
      </div>
      {offer instanceof BoxOfferDetailedEntity && <BoxOfferArchivedModal ref={boxOfferArchivedModal} offer={offer} />}
    </>
  ) : null
}
